/**
 * The round transition. SPEC.md §4.4, §4.3, §19, §20.4.
 *
 * A hand ends itself the moment the table has told it enough, and the next is
 * dealt in the same transaction (§4.3) — so by the time anything can be shown,
 * the hand it would be about is already gone and a new one is on the keys. This
 * is what marks that boundary: forty-two playing cards thrown out of a jackpot
 * flash, half a second, and nothing to read or dismiss.
 *
 * It replaced a snackbar line ("HAND 4 closed · +38 / -38"), and the reason is
 * the lane rather than the wording. That lane is where an entry's UNDO arrives,
 * so a notice in it trains the table to look for a control — and this one never
 * had one, because a hand that has ended is finished and there is nothing left
 * to withdraw (§12.5). The accounting it carried has not gone anywhere: the
 * scores on the roster move as it plays, and the whole hand is one tap away in
 * history. §12.1, §13.1, §14.1
 *
 * **It is decoration, and decoration is never load-bearing.** Nothing here is a
 * hit target, nothing here has an accessible name, and nothing here is the only
 * way to learn anything. `hand-closes-itself.spec.ts` asserts a hand closes with
 * nothing to dismiss anywhere at the table, which is the product's central
 * claim; a layer that could be tapped, focused or announced would break it. §4.4
 *
 * Three things can be true of the table at once and none of them may be mistaken
 * for another: the **dealer** wears a warm glow (§9.5), the **hand's winner**
 * dances on their own podium for thirty seconds (§4.5), and **the round has just
 * turned over** — which is this, and which is over in half a second. They are
 * driven from three different facts and share no marker.
 */

import { useEffect, useRef } from 'react';
import {
  JACKPOT_GLINT_COUNT,
  JACKPOT_IMPACT,
  JACKPOT_REDUCED_DURATION_MS,
  JACKPOT_TEARDOWN_MS,
  jackpotCardFaces,
  jackpotCardFlight,
  jackpotGlintFlight,
  type JackpotBounds,
} from './jackpot';
import { endRoundExplosion, useRoundExplosion } from '@/stores/ui-store';

/**
 * The faces, computed once for the module rather than once per render. §20.4
 *
 * They never change — forty-two fixed ranks in a fixed order — and rebuilding
 * the array on every render of a screen that re-renders on every keypress would
 * be forty-two objects a tap, to draw the same forty-two cards.
 */
const FACES = jackpotCardFaces();
const GLINTS = Array.from({ length: JACKPOT_GLINT_COUNT }, (_, index) => index);

/**
 * Whether the device has asked not to be moved, read at the moment of firing.
 *
 * The blanket `prefers-reduced-motion` rule in index.css collapses CSS
 * animations and cannot touch these: they are `Element.animate` calls, which no
 * stylesheet governs. So the preference has to be honoured here, in the code
 * that starts them, or the one guarantee §19 makes is quietly false for the one
 * effect on the screen that moves fifty things at once.
 */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Whether this engine can be asked to run the effect at all.
 *
 * Decoration is never allowed to be the thing that throws. `Element.animate` and
 * `Element.getAnimations` have been everywhere this app runs for years, but the
 * cost of asking is one `typeof` and the cost of being wrong is an exception on
 * the frame a hand ends — which would take the score screen down with it, at the
 * one moment the table is looking at it. §4.4
 */
function canAnimate(root: HTMLElement): boolean {
  return typeof root.animate === 'function' && typeof root.getAnimations === 'function';
}

function cancelAll(root: HTMLElement): void {
  for (const animation of root.getAnimations({ subtree: true })) animation.cancel();
}

export function RoundExplosion() {
  const explosion = useRoundExplosion();
  const layer = useRef<HTMLDivElement | null>(null);
  /**
   * How many bursts have been drawn here, and the last hand one was drawn for.
   *
   * Published on the element, because "exactly once per completed hand" is the
   * whole contract of this effect and is otherwise unobservable a second later —
   * a resync or a duplicated frame that fired a second burst looks identical to
   * one that did not. `plays` counts; `round` names. §42.2
   */
  const plays = useRef(0);

  useEffect(() => {
    const element = layer.current;
    if (!element || !explosion || !canAnimate(element)) return;

    /**
     * A boundary this device has already sat through is not one to draw. §4.4
     *
     * The store's latch stops the same hand being *started* twice, and it cannot
     * stop this: the slot holds a completion for the length of the burst, and
     * anything that mounts a second layer inside that window — a remount, a
     * screen that came back — would read a live explosion and throw its cards
     * for a hand the table has already watched end. Elapsed time is the honest
     * question, because the effect is a moment rather than a state, and a moment
     * that has passed cannot be entered again. §12.1
     */
    if (Date.now() - explosion.startedAt > JACKPOT_TEARDOWN_MS) return;

    /*
      Whatever is still in the air belongs to the hand before this one. Cancelled
      rather than left to finish: two overlapping bursts is not twice the effect,
      it is a screen of cards with no impact anybody can point at. §20.4
    */
    cancelAll(element);

    const bounds: JackpotBounds = {
      width: element.clientWidth,
      height: element.clientHeight,
    };
    // A layer with no size yet — the very first paint, or a screen that is not on
    // screen — has nowhere to throw anything. Better nothing than forty-two cards
    // stacked on a single point.
    if (bounds.width <= 0 || bounds.height <= 0) return;

    const reduced = prefersReducedMotion();

    element.dataset.firing = '';
    plays.current += 1;
    element.dataset.explosionPlays = String(plays.current);
    element.dataset.roundExplosion = String(explosion.handNumber);
    /*
      Which of the two boundaries this device drew, and it is published for the
      same reason the count is: it outlives the animation. The alternative is a
      test that races a 320 ms window to catch `getAnimations()` mid-flight, and
      a racy assertion about reduced motion fails in the direction that hides the
      regression. §19, §42.2
    */
    element.dataset.explosionMode = reduced ? 'reduced' : 'full';

    const fire = (selector: string, keyframes: Keyframe[], options: KeyframeAnimationOptions) => {
      const target = element.querySelector<HTMLElement>(selector);
      target?.animate(keyframes, { fill: 'both', ...options });
    };

    if (reduced) {
      /*
        The substitute, and it still says the thing. §19

        No cards, no spin, no sunburst — one soft flash at the centre and the
        wash behind it, in a third of a second. What the burst communicates is
        "the round has turned over", and a light that arrives and leaves says
        that without moving fifty objects across the field of view of somebody
        who has asked for exactly that not to happen.
      */
      fire('.jackpot-core', [{ opacity: 0 }, { opacity: 0.85, offset: 0.3 }, { opacity: 0 }], {
        duration: JACKPOT_REDUCED_DURATION_MS,
        easing: 'ease-out',
      });
      fire('.jackpot-wash', [{ opacity: 0 }, { opacity: 0.6, offset: 0.25 }, { opacity: 0 }], {
        duration: JACKPOT_REDUCED_DURATION_MS,
        easing: 'ease-out',
      });
    } else {
      for (const [part, flight] of Object.entries(JACKPOT_IMPACT)) {
        fire(`.jackpot-${part}`, flight.keyframes, {
          duration: flight.durationMs,
          easing: flight.easing,
        });
      }

      const cards = element.querySelectorAll<HTMLElement>('.jackpot-card');
      cards.forEach((card, index) => {
        const flight = jackpotCardFlight(index, bounds);
        card.animate(flight.keyframes, {
          duration: flight.durationMs,
          delay: flight.delayMs,
          easing: flight.easing,
          fill: 'both',
        });
      });

      const glints = element.querySelectorAll<HTMLElement>('.jackpot-glint');
      glints.forEach((glint, index) => {
        const flight = jackpotGlintFlight(index, bounds);
        glint.animate(flight.keyframes, {
          duration: flight.durationMs,
          delay: flight.delayMs,
          easing: flight.easing,
          fill: 'both',
        });
      });
    }

    /*
      Put away afterwards rather than left holding its last frame. Every
      animation ends at opacity 0, so nothing is visible either way — but a layer
      of fifty elements that is still `visibility: visible` and still carrying
      `will-change` is fifty compositor layers the browser keeps alive under a
      keypad, for a burst that finished half a second ago. §20.4
    */
    const settle = window.setTimeout(
      () => {
        cancelAll(element);
        delete element.dataset.firing;
        endRoundExplosion();
      },
      reduced ? JACKPOT_REDUCED_DURATION_MS + 60 : JACKPOT_TEARDOWN_MS,
    );

    return () => {
      window.clearTimeout(settle);
    };
    // The identity of the hand that turned over. A re-render is not one, and a
    // resync landing on the same summary is not one either. §4.5, §12.1
  }, [explosion]);

  return (
    <div
      ref={layer}
      /*
        Never a hit target and never announced. The keypad is live underneath
        this for the whole half second — a player mid-report must be able to
        finish typing through it — and a screen reader must not be handed
        forty-two suit glyphs. §9.3, §19, §18.4
      */
      aria-hidden="true"
      className="jackpot-layer"
    >
      <i className="jackpot-wash" />
      <i className="jackpot-sun" />
      <i className="jackpot-halo" />
      <i className="jackpot-core" />

      {/*
        Built once and reused for every hand of the night, rather than mounted
        and unmounted around each burst. Fifty elements created and destroyed on
        a hand boundary is fifty allocations and a style recalculation landing in
        the same frame as the new hand's first render — on the screen that is not
        allowed to reflow. Idle they cost a stylesheet rule and no paint: opacity
        zero, no `will-change`, and the layer itself `visibility: hidden`. §20.4
      */}
      {FACES.map((face, index) => (
        <i
          key={index}
          className={`jackpot-card${face.red ? ' is-red' : ''}`}
          data-rank={face.rank}
          data-suit={face.suit}
        >
          {face.suit}
        </i>
      ))}

      {GLINTS.map((index) => (
        <i key={index} className="jackpot-glint" />
      ))}
    </div>
  );
}
